import openSocket from "socket.io-client";
import { getBackendUrl } from "../config";
import { getAccessToken } from "./authToken";

let sharedSocket = null;
let activeLeases = 0;
let disconnectTimer = null;

const createInactiveSocketLease = () => {
  const lease = {
    id: undefined,
    connected: false,
    disconnected: true,
    auth: {},
    io: {
      on() {
        return this;
      },
      off() {
        return this;
      }
    },
    connect() {
      return lease;
    },
    on() {
      return lease;
    },
    once() {
      return lease;
    },
    off() {
      return lease;
    },
    emit() {
      return false;
    },
    disconnect() {
      return lease;
    }
  };

  return lease;
};

function getSocketAuth() {
  const token = getAccessToken();
  return token ? { token } : {};
}

function syncSocketAuth(socket) {
  if (socket) {
    socket.auth = getSocketAuth();
  }
}

function ensureSharedSocket() {
  const auth = getSocketAuth();
  if (!auth.token) {
    return null;
  }

  if (sharedSocket) {
    syncSocketAuth(sharedSocket);
    if (!sharedSocket.connected && sharedSocket.disconnected) {
      sharedSocket.connect();
    }
    return sharedSocket;
  }

  sharedSocket = openSocket(getBackendUrl(), {
    transports: ["websocket", "polling"],
    auth
  });

  const syncAuth = () => syncSocketAuth(sharedSocket);

  if (sharedSocket.io) {
    sharedSocket.io.on("reconnect_attempt", syncAuth);
  }

  sharedSocket.on("connect_error", syncAuth);

  return sharedSocket;
}

// Socket.IO authenticates once, during the handshake. Swapping the stored token
// (entering or leaving support mode) updates `socket.auth` but leaves the live
// connection on the previous identity, so the server keeps applying the old
// permissions until it reconnects. Forcing a reconnect re-runs the handshake and
// re-fires `connect` on every lease, which makes each component re-emit its
// joinChatBox/joinNotification/joinTickets with the new identity.
//
// This cannot be done through a lease: lease.disconnect() only tears down the
// shared socket once activeLeases reaches zero.
export const refreshSocketIdentity = () => {
  if (!sharedSocket || !getSocketAuth().token) {
    return;
  }

  syncSocketAuth(sharedSocket);
  sharedSocket.disconnect();
  sharedSocket.connect();
};

function connectToSocket() {
  if (!getSocketAuth().token) {
    return createInactiveSocketLease();
  }

  if (disconnectTimer) {
    clearTimeout(disconnectTimer);
    disconnectTimer = null;
  }

  const socket = ensureSharedSocket();
  if (!socket) {
    return createInactiveSocketLease();
  }

  activeLeases += 1;
  const socketListeners = [];
  const managerListeners = [];
  let closed = false;

  const managerProxy = socket.io
    ? {
        on(eventName, handler) {
          socket.io.on(eventName, handler);
          managerListeners.push({ eventName, handler });
          return this;
        },
        off(eventName, handler) {
          socket.io.off(eventName, handler);
          const index = managerListeners.findIndex(
            item => item.eventName === eventName && item.handler === handler
          );
          if (index >= 0) managerListeners.splice(index, 1);
          return this;
        }
      }
    : socket.io;

  const lease = {
    get id() {
      return socket.id;
    },
    get connected() {
      return socket.connected;
    },
    get disconnected() {
      return socket.disconnected;
    },
    get auth() {
      return socket.auth;
    },
    set auth(value) {
      socket.auth = value;
    },
    get io() {
      return managerProxy;
    },
    connect() {
      syncSocketAuth(socket);
      return socket.connect();
    },
    on(eventName, handler) {
      socket.on(eventName, handler);
      socketListeners.push({ eventName, handler });
      if (eventName === "connect" && socket.connected) {
        setTimeout(() => {
          if (!closed) handler();
        }, 0);
      }
      return lease;
    },
    once(eventName, handler) {
      const wrappedHandler = (...args) => {
        lease.off(eventName, wrappedHandler);
        handler(...args);
      };
      return lease.on(eventName, wrappedHandler);
    },
    off(eventName, handler) {
      socket.off(eventName, handler);
      const index = socketListeners.findIndex(
        item => item.eventName === eventName && item.handler === handler
      );
      if (index >= 0) socketListeners.splice(index, 1);
      return lease;
    },
    emit(...args) {
      return socket.emit(...args);
    },
    disconnect() {
      if (closed) return lease;
      closed = true;

      socketListeners.splice(0).forEach(({ eventName, handler }) => {
        socket.off(eventName, handler);
      });

      if (socket.io) {
        managerListeners.splice(0).forEach(({ eventName, handler }) => {
          socket.io.off(eventName, handler);
        });
      }

      activeLeases = Math.max(0, activeLeases - 1);

      if (activeLeases === 0) {
        disconnectTimer = setTimeout(() => {
          if (activeLeases === 0 && sharedSocket) {
            sharedSocket.disconnect();
            sharedSocket = null;
          }
        }, 500);
      }

      return lease;
    }
  };

  return lease;
}

export default connectToSocket;
